import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConvertHelper, DotNetBridge } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { UserFriendlyError } from '@barthauer/abc/core/error-handling';
import { ISupportsRequestHeaderValues } from '@barthauer/basys/core/authentication';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, switchMap, take, tap } from 'rxjs/operators';
import { ProfileSelectionComponentInfo } from '../profile-selection/profile-selection-component-info';
import { ProfileSettingsService } from '../services/profile-settings.service';
import { UserService } from '../services/user.service';
import { WebApiService } from '../services/webapi.service';
import { LoginComponentInfo } from './../login/login-component-info';
import { LoginSettingsService } from './../services/login-settings.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private static readonly REFRESH_INDICATOR = new Object();
  private isRefreshTokenInProgress = false;
  private refreshTokenSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private globalConfig: AbcConfigService,
    private router: Router,
    private loginSettingsService: LoginSettingsService,
    private profileSettingsService: ProfileSettingsService,
    private userService: UserService
  ) {}
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.addAuthHeadersAndHandleRequest(request, next).pipe(
      catchError((err) => this.handleRequestError(request, next, err, false))
    );
  }

  private addAuthHeadersAndHandleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let handledRequest$: Observable<HttpEvent<any>> = null;

    if (this.globalConfig?.isInitialized) {
      const avoidAuthHeaders = ConvertHelper.toBoolean(
        request.headers.get(WebApiService.HTTP_HEADER_AVOID_AUTH_HEADERS)
      );

      if (!avoidAuthHeaders) {
        handledRequest$ = this.loginSettingsService.getCurrentLoginSettings().pipe(
          concatMap((loginSettings) => {
            let headers: HttpHeaders = request.headers.set('Content-Type', 'application/json'); // Just ensure

            // Login token
            if (loginSettings && loginSettings.access_token && !loginSettings.isAnonymousLogin) {
              headers = headers.set(
                LoginSettingsService.HTTP_HEADER_AUTH_TOKEN,
                `Bearer ${loginSettings.access_token}`
              );
            }

            // Current profile (if supported)
            return this.profileSettingsService.getCurrentProfileSettings().pipe(
              concatMap((profileSettings) => {
                let profileId: string;
                const headerValues = request.body as ISupportsRequestHeaderValues;
                if (headerValues?.getRequestHeaderValue) {
                  profileId = headerValues.getRequestHeaderValue(ProfileSettingsService.HTTP_HEADER_PROFILE_ID);
                }
                if (!profileId && profileSettings) {
                  profileId = profileSettings.id;
                }

                if (profileId) {
                  headers = headers.set(ProfileSettingsService.HTTP_HEADER_PROFILE_ID, profileId);
                }

                request = request.clone({ headers: headers });

                return next.handle(request);
              })
            );
          })
        );
      }
    }

    if (!handledRequest$) {
      handledRequest$ = next.handle(request);
    }

    return handledRequest$;
  }

  private handleRequestError(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: any,
    isDirectlyAfterRefreshToken: boolean
  ): Observable<any> {
    if (err instanceof HttpErrorResponse) {
      if (this.isUnauthorizedError(err)) {
        return this.handleUnauthorizedError(request, next, err, isDirectlyAfterRefreshToken);
      } else if (this.profileSettingsService.hasProfileNotFoundMessage(err)) {
        return this.handleProfileNotFoundError(request, next, err, isDirectlyAfterRefreshToken);
      }
    }

    return throwError(err);
  }

  private isUnauthorizedError(err: HttpErrorResponse): boolean {
    return err.status === 401 || err.status === 403 || this.isTokenUrl(err.url);
  }

  public static readonly LoginRedirectModeKey = 'LoginRedirectMode';
  private handleUnauthorizedError(
    request: HttpRequest<any>,
    next: HttpHandler,
    status401Error: HttpErrorResponse,
    isDirectlyAfterRefreshToken: boolean
  ): Observable<any> {
    if (this.isTokenUrl(request.url) || isDirectlyAfterRefreshToken) {
      // 401 on login or refresh auth token call --> force logout and re-login
      return this.loginSettingsService.removeCurrentLoginSettings().pipe(
        concatMap(() => {
          if (!LoginComponentInfo.isCurrentPageLoginPage(this.router)) {
            sessionStorage.setItem(
              AuthenticationInterceptor.LoginRedirectModeKey,
              UserService.TOKEN_GRANT_TYPE_REFRESHTOKEN
            );
            AuthenticationInterceptor.reload('/');
          }

          if (isDirectlyAfterRefreshToken) {
            try {
              const isCordova = typeof window !== 'undefined' && !!(window as any)?.cordova;
              const mfaFlag = typeof window !== 'undefined' ? localStorage.getItem('mfaInProgress') : null;

              if (isCordova && mfaFlag === '1') {
                const message =
                  'Die App wurde beim letzten Mal während der Zwei-Faktor-Anmeldung geschlossen. ' +
                  'Sie wurden automatisch abgemeldet. Bitte melden Sie sich erneut an.';

                try {
                  localStorage.removeItem('mfaInProgress');
                } catch {}

                return throwError(() => new UserFriendlyError(message));
              }
            } catch {}

            return throwError(
              () =>
                new UserFriendlyError(
                  'REFRESH_TOKEN DEBUG: Refreshed auth token was invalidated immediately.',
                  status401Error
                )
            );
          } else {
            return throwError(status401Error);
          }
        })
      );
    } else if (this.isRefreshTokenInProgress) {
      // 401 on non-auth calls while refreshing auth token --> hold this and subsequent API calls until new auth token was fetched
      return this.refreshTokenSubject$.pipe(
        filter((result) => result != null),
        take(1),
        switchMap(() =>
          this.addAuthHeadersAndHandleRequest(request, next).pipe(
            tap(() =>
              this.logMessage(`REFRESH_AUTH_TOKEN_DEBUG: Deferred request ${request.url} after auth tokens refreshed.`)
            ),
            catchError((err) => this.handleRequestError(request, next, err, true))
          )
        )
      );
    } else if (DotNetBridge.hasContext) {
      console.log('CONTEXT DEBUG: Unauthorized.');
      return of(undefined);
    } else {
      // 401 on non-auth calls --> Refresh auth token and use it for this and subsequent API calls
      this.isRefreshTokenInProgress = true;
      this.refreshTokenSubject$.next(null); // Subsequent calls will wait until after refresh

      return this.userService.refreshCurrentUser().pipe(
        // Refreshes and stores new tokens
        concatMap((isLoggedIn) => {
          this.isRefreshTokenInProgress = false;

          if (isLoggedIn) {
            console.log('Authentication infos will be refreshed and requests rehandled.');
            this.logMessage(`REFRESH_AUTH_TOKEN_DEBUG: Refreshing auth tokens before request ${request.url}.`);
            this.refreshTokenSubject$.next(AuthenticationInterceptor.REFRESH_INDICATOR); // Releases calls on hold
            return this.addAuthHeadersAndHandleRequest(request, next).pipe(
              tap(() =>
                this.logMessage(
                  `REFRESH_AUTH_TOKEN_DEBUG: Resubmitting request ${request.url} after auth tokens refreshed.`
                )
              ),
              catchError((err) => this.handleRequestError(request, next, err, true))
            );
          } else {
            // Redirect to Login not necessary (will be done in Token call error handler)
            // CHE: Uncomment it as this throws an error if the storage is empty.
            if (!LoginComponentInfo.isCurrentPageLoginPage(this.router)) {
              this.router.navigate(['/']); // Go to login page (AuthenticationGuard is not guaranteed, e.g. Profile selection page)
            }
            return throwError(status401Error);
          }
        })
      );
    }
  }

  private isTokenUrl(url: string) {
    return url?.split('?')[0]?.endsWith('/Token');
  }

  private handleProfileNotFoundError(
    request: HttpRequest<any>,
    next: HttpHandler,
    profileNotFoundError: HttpErrorResponse,
    isDirectlyAfterRefreshToken: boolean
  ): Observable<any> {
    if (!ProfileSelectionComponentInfo.areProfilesSupported(this.router)) {
      return of(null).pipe(
        concatMap((_) => {
          return throwError(profileNotFoundError);
        })
      );
    }
    // Goto profile selection page if invalid profile id was used
    return this.profileSettingsService.removeCurrentProfileSettings().pipe(
      concatMap(() => {
        // Avoid endless loading loop on certain WebApi calls
        if (!ProfileSelectionComponentInfo.isCurrentPageProfileSelectionPage(this.router)) {
          AuthenticationInterceptor.reload();
        }

        return throwError(profileNotFoundError);
      })
    );
  }

  // tslint:disable-next-line: member-ordering
  private readonly doShowLogMessages = false;
  private logMessage(text: string): void {
    if (this.doShowLogMessages) {
      console.log(text);
    }
  }

  // tslint:disable-next-line: member-ordering
  public static getAuthenticationHeaders(
    userService: UserService,
    profileSettingsService: ProfileSettingsService
  ): Observable<{ [name: string]: any }> {
    return userService.getCurrentFreshLoginSettings().pipe(
      concatMap((loginSettings) => {
        const result: { [name: string]: any } = {};

        // Login token
        if (loginSettings && loginSettings.access_token && !loginSettings.isAnonymousLogin) {
          result[LoginSettingsService.HTTP_HEADER_AUTH_TOKEN] = `Bearer ${loginSettings.access_token}`;
        }

        // Current profile (if supported)
        return profileSettingsService.getCurrentProfileSettings().pipe(
          concatMap((profileSettings) => {
            if (profileSettings && profileSettings.id) {
              result[ProfileSettingsService.HTTP_HEADER_PROFILE_ID] = profileSettings.id;
            }
            return of(result);
          })
        );
      })
    );
  }

  static readonly CurrentUrlKey = 'currentUrl';
  public static notifyUrl(): void {
    if (!navigator['app']) {
      const url = window.location?.href;
      sessionStorage.setItem(AuthenticationInterceptor.CurrentUrlKey, url);
    }
  }

  public static resetUrl(): void {
    if (!navigator['app']) {
      sessionStorage.removeItem(AuthenticationInterceptor.CurrentUrlKey);
    }
  }

  public static reload(routePath?: string): void {
    let currentUrl = sessionStorage.getItem(AuthenticationInterceptor.CurrentUrlKey);
    if (currentUrl) {
      sessionStorage.removeItem(AuthenticationInterceptor.CurrentUrlKey);
      if (currentUrl.endsWith(')') && !currentUrl.includes('?')) {
        const pos = currentUrl.lastIndexOf('(');
        if (pos > 0) {
          currentUrl = currentUrl.substring(0, pos - 1);
        }
      }
      if (currentUrl.endsWith('/login')) {
        currentUrl = currentUrl.substring(0, currentUrl.length - '/login'.length);
      }
      location.assign(currentUrl);
    } else {
      const orig = location.href;
      let path = orig.split('?')[0].split('#')[0].split('(')[0];
      const pos = path.lastIndexOf('/');
      path = path.substring(0, pos);
      if (path.endsWith('/login')) {
        path = path.substring(0, path.length - '/login'.length);
      }
      if (routePath) {
        path += routePath;
      }
      if (navigator['app']) {
        if (!path.endsWith('/')) {
          path += '/';
        }
        path += 'index.html';
      }
      if (path === orig) {
        location.reload();
      } else {
        location.assign(path);
      }
    }
  }
}
